import { getApiEndpoint } from '../config/apiEndpoints';
import { fetchWithAuth } from '../utils/tokenRefresh';

export interface ListItem {
  uuid: string;
  list_uuid: string;
  title: string;
  notes: string;
  rank: number;
  is_honorable_mention: boolean;
  created_at: string;
  updated_at: string;
}

export interface ListData {
  uuid: string;
  username: string;
  name: string;
  list_type: 'ranked' | 'unranked';
  notes: string;
  created_at: string;
  updated_at: string;
  items?: ListItem[];
  honorable_mentions?: ListItem[];
}

export interface HistoryEvent {
  list_uuid: string;
  timestamp_event_id: string;
  event_type: string;
  timestamp: string;
  description: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
}

export interface Candidate {
  uuid: string;
  list_uuid: string;
  title: string;
  notes: string;
  source: string;
  status: string;
  created_at: string;
}

export async function getLists(): Promise<ListData[]> {
  const response = await fetchWithAuth(getApiEndpoint('/lists'));
  const data = await response.json();
  return data.lists;
}

export async function createList(name: string, listType: string, notes: string = ''): Promise<ListData> {
  const response = await fetchWithAuth(getApiEndpoint('/lists'), {
    method: 'POST',
    body: JSON.stringify({ name, list_type: listType, notes }),
  });
  return response.json();
}

export async function getList(id: string): Promise<ListData> {
  const response = await fetchWithAuth(getApiEndpoint(`/lists/${id}`));
  return response.json();
}

export async function updateList(id: string, updates: Partial<ListData>): Promise<ListData> {
  const response = await fetchWithAuth(getApiEndpoint(`/lists/${id}`), {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return response.json();
}

export async function deleteList(id: string): Promise<void> {
  await fetchWithAuth(getApiEndpoint(`/lists/${id}`), { method: 'DELETE' });
}

export async function addItem(listId: string, title: string, notes: string = '', rank: number = 0, isHonorableMention: boolean = false): Promise<ListItem> {
  const response = await fetchWithAuth(getApiEndpoint(`/lists/${listId}/items`), {
    method: 'POST',
    body: JSON.stringify({ title, notes, rank, is_honorable_mention: isHonorableMention }),
  });
  return response.json();
}

export async function updateItem(listId: string, itemId: string, updates: Partial<ListItem>): Promise<ListItem> {
  const response = await fetchWithAuth(getApiEndpoint(`/lists/${listId}/items/${itemId}`), {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return response.json();
}

export async function deleteItem(listId: string, itemId: string): Promise<void> {
  await fetchWithAuth(getApiEndpoint(`/lists/${listId}/items/${itemId}`), { method: 'DELETE' });
}

export async function reorderItems(listId: string, order: string[]): Promise<void> {
  await fetchWithAuth(getApiEndpoint(`/lists/${listId}/reorder`), {
    method: 'PUT',
    body: JSON.stringify({ order }),
  });
}

export async function getListHistory(listId: string): Promise<HistoryEvent[]> {
  const response = await fetchWithAuth(getApiEndpoint(`/lists/${listId}/history`));
  const data = await response.json();
  return data.events;
}

export async function getAllHistory(): Promise<HistoryEvent[]> {
  const response = await fetchWithAuth(getApiEndpoint('/history'));
  const data = await response.json();
  return data.events;
}

export async function exportMarkdown(listId: string): Promise<string> {
  const response = await fetchWithAuth(getApiEndpoint(`/lists/${listId}/export/markdown`));
  const data = await response.json();
  return data.markdown;
}

export async function getCandidates(): Promise<Candidate[]> {
  const response = await fetchWithAuth(getApiEndpoint('/candidates'));
  const data = await response.json();
  return data.candidates;
}

export async function acceptCandidate(candidateId: string): Promise<void> {
  await fetchWithAuth(getApiEndpoint(`/candidates/${candidateId}/accept`), { method: 'POST' });
}

export async function rejectCandidate(candidateId: string): Promise<void> {
  await fetchWithAuth(getApiEndpoint(`/candidates/${candidateId}/reject`), { method: 'POST' });
}
